import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/ui/primitives';
import { PlusOutlined } from '@/shared/ui/icons';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import BlockedUsersTable from '../components/BlockedUsersTable';
import { useDeleteBlockedUser } from '../hooks/useBlockedUsersMutations';
import DynamicFilterBuilder from '@/shared/filtering/DynamicFilterBuilder';
import { ADMIN_FILTER_RESOURCES, type DynamicRelationLookups } from '@/shared/filtering/types';
import { useDynamicAdminList } from '@/shared/filtering/useDynamicAdminList';
import { userApi } from '@/features/users/api/user.api';

export default function BlockedUsersListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  const list = useDynamicAdminList<any>(ADMIN_FILTER_RESOURCES.blockedUsers, { initialSortField: 'createdAt', initialSortDir: 'desc' });
  const { data, isLoading } = list.searchQuery as any;
  const deleteMutation = useDeleteBlockedUser();

  const relationLookups = useMemo<DynamicRelationLookups>(() => ({
    'blockedBy.id': {
      fetchFn: ({ search, page, size }) => userApi.list({ page, size, firstName: search }),
      mapOption: (u: any) => ({ value: u.id, label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || `#${u.id}` }),
      placeholder: t('blockedUsers.blockedByLabel'),
    },
    'blockedUser.id': {
      fetchFn: ({ search, page, size }) => userApi.list({ page, size, firstName: search }),
      mapOption: (u: any) => ({ value: u.id, label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || `#${u.id}` }),
      placeholder: t('blockedUsers.blockedUserLabel'),
    },
  }), [t]);

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, { onSettled: () => setDeleteTarget(null) });
  };

  return (
    <div>
      <PageHeader
        title={t('blockedUsers.title')}
        subtitle={t('blockedUsers.subtitle')}
        action={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/blocked-users/create')}>
            {t('blockedUsers.add')}
          </Button>
        }
      />

      <DynamicFilterBuilder resource={list.resource} metadata={list.metadataQuery.data}
        value={list.filters}
        onChange={list.setFilters}
        onReset={list.resetFilters}
        relationLookups={relationLookups}
      />

      <div style={{ marginTop: 16 }}>
        <BlockedUsersTable
          data={data?.content || []}
          loading={isLoading}
          sortField={list.sortField}
          sortDir={list.sortDir}
          onSort={list.handleSort}
          onDelete={(item: any) => setDeleteTarget(item)}
          page={data?.currentPage ?? 0}
          pageSize={list.size}
          total={data?.totalItems ?? 0}
          onPageChange={list.setPage}
        />
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('blockedUsers.deleteTitle')}
        message={t('blockedUsers.deleteMessage', { blockedUser: deleteTarget?.blockedUserName, blockedBy: deleteTarget?.blockedByName })}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
